import { onCLS, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';
import { ANALYTICS_CONFIG } from './config';

// Thresholds per Google's recommendations (FID deprecated, replaced by INP)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },   // Largest Contentful Paint (ms)
  CLS: { good: 0.1, poor: 0.25 },    // Cumulative Layout Shift (score)
  TTFB: { good: 800, poor: 1800 },   // Time to First Byte (ms)
  INP: { good: 200, poor: 500 },     // Interaction to Next Paint (ms)
};

type VitalName = keyof typeof THRESHOLDS;

/**
 * Get rating based on metric value
 */
function getRating(name: VitalName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Report metric to Plausible Analytics
 */
async function reportToPlausible(metric: Metric): Promise<void> {
  // Check consent first
  const { consentManager } = await import('./consent');
  if (!consentManager.hasConsented('analytics')) {
    console.log('[WebVitals] Metric blocked (no consent):', metric.name);
    return;
  }

  const name = metric.name as VitalName;
  const rating = getRating(name, metric.value);
  
  // Round value for cleaner reporting
  const value = Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value);

  console.log(`[WebVitals] ${name}: ${value} (${rating})`);

  // Send to Plausible if available
  if (typeof window !== 'undefined' && (window as any).plausible) {
    try {
      (window as any).plausible('Web Vitals', {
        props: {
          metric: name,
          value,
          rating,
          page: window.location.pathname,
        },
      });
    } catch (error) {
      console.error('[WebVitals] Failed to report:', error);
    }
  }
}

/**
 * Initialize Core Web Vitals monitoring
 * Call this once in your app's entry point
 */
export function initWebVitals(): void {
  // Only run in production to avoid noise in dev
  if (!import.meta.env.PROD && !ANALYTICS_CONFIG.enabled) {
    console.log('[WebVitals] Skipped in development');
    return;
  }

  try {
    // Largest Contentful Paint - loading performance
    onLCP(reportToPlausible);
    
    // Cumulative Layout Shift - visual stability
    onCLS(reportToPlausible);
    
    // Time to First Byte - server responsiveness
    onTTFB(reportToPlausible);
    
    // Interaction to Next Paint - overall responsiveness (Core Web Vital since 2024)
    onINP(reportToPlausible);
    
    console.log('[WebVitals] Monitoring initialized');
  } catch (error) {
    console.error('[WebVitals] Failed to initialize:', error);
  }
}

/**
 * Get current performance summary for debugging
 */
export function getPerformanceSummary(): Record<string, number> {
  const summary: Record<string, number> = {};
  
  if (typeof window !== 'undefined' && window.performance) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      summary.dns = Math.round(navigation.domainLookupEnd - navigation.domainLookupStart);
      summary.tcp = Math.round(navigation.connectEnd - navigation.connectStart);
      summary.ttfb = Math.round(navigation.responseStart - navigation.requestStart);
      summary.domLoad = Math.round(navigation.domContentLoadedEventEnd - navigation.startTime);
      summary.fullLoad = Math.round(navigation.loadEventEnd - navigation.startTime);
    }
  }
  
  return summary;
}
